import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import tw from 'twin.macro';

import 'react-base-table/styles.css';

import { ToolTip } from '@insights_module/components/ToolTip';
import { SearchBar } from '@insights_module/components/SearchBar';
import {
  SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS,
  ShowDetailsTableView,
  TABLE_VIEW,
  useShowDetailsContext,
} from '@insights_module/context/ShowDetailsContext';
import { useInsightsContext } from '@insights_module/context/InsightsContext';
import { SORT_ORDER } from '@insights_module/constants/defaults';
import { useShowDetailsTableData } from '@insights_module/hooks/useShowDetailsTableData';
import { FEATURE_FLAG, useFeatureFlag } from '@auth_module/hooks/useFeatureFlag';
import { Loader } from '@components/Loader';
import { CloseIcon } from '@icons/CloseIcon';
import { GARM_CATEGORIES } from '@insights_module/constants/garm';
import { Info } from '@icons/Info';
import { NoTableData } from '@insights_module/components/NoTableData';
import { CategoryType } from '@insights_module/types/BrandSafety';
import useOnClickOutside from '@app/hooks/useOnClickOutside';
import { Sidebar } from '@insights_module/components/Sidebar';

import { TabSwitch } from '@insights_module/components/TabSwitch';
import { AudiosSortKeyType, TableViewType } from '@insights_module/types/LibraryTable';
import {
  AUDIOS_SORTABLE_KEY,
  BaseCell,
  BrandSafetyCell,
  BrandSafetyLegend,
  ContextualDataCell,
  DetailsCell,
  EntitiesDataCell,
} from '@insights_module/components/LibraryTable';
import { useMyLibraryContext } from '@insights_module/context/MyLibraryContext';
import { SortableHead } from '@insights_module/components/Table';
import { Pagination } from '@components/Pagination';
import usePagination from '@app/hooks/usePagination';
import { convertSecondsToHMS } from '@utils/dateUtils';
import { BrandSafetyRiskBadge } from '@insights_module/components/BrandSafetyRiskBadge';
import { TableIcon } from '@icons/TableIcon';
import { CheckMarkIcon } from '@icons/CheckMarkIcon';
import { ExportSelect } from '@insights_module/components/shows_screen/ExportsSelect';

export const TABLE_TITLE_HEIGHT = '38';
export const TABLE_ROW_HEIGHT = '77';
export const DRAWER_HEADER_HEIGHT = '44';
export const TABLE_BODY_TOP_OFFSET = '66';

const TAB_SWITCH_TABS = Object.values(TABLE_VIEW).map((tab) => ({
  id: tab,
  label: <Trans i18nKey={`insights.show_details_drawer.tabs.${tab}`}>{tab}</Trans>,
}));

const TableWrapper = tw.div`relative flex flex-col h-full w-[calc(100%-371px)] bg-neutral-background`;
const TableTitle = tw.div`flex w-full border-b border-gray-3 bg-neutral-background justify-between items-center px-[12px] py-[6px] text-sm font-semibold z-10`;
const TableInnerWrapper = tw.div`h-full overflow-scroll w-full`;
const LoadingOverlay = tw.div`absolute top-[56px] left-0 right-0 bottom-0 z-[29] flex items-center justify-center bg-neutral-background`;
const QueryLabelContainer = tw.div`bg-gray-3 rounded flex flex-row items-center justify-between text-[11px] px-2 py-[6px] max-h-[25px] truncate gap-2 font-normal max-w-[400px]`;
const QueryLabel = tw.span`text-text-and-icon truncate`;
const CloseButton = tw.div`flex items-center justify-center w-6 h-6 cursor-pointer`;
const Separator = tw.span`inline-block w-[1px] h-4 bg-gray-3`;
const TooltipContent = tw.div`text-[11px] w-auto max-w-[180px]`;

const StyledTable = tw.table`flex flex-col w-full h-full grow overflow-hidden`;
const StyledTHead = tw.thead`border-b border-solid border-gray-1 flex gap-[2px] flex-col`;
const StyledTBody = tw.tbody`border-b border-solid border-gray-1 w-full max-h-full overflow-y-scroll flex flex-col gap-[2px] grow`;
const StyledTFoot = tw.tfoot`sticky w-full bottom-0 right-0 px-[12px] py-[6px] bg-neutral-background flex flex-col items-center justify-end lg:justify-between lg:flex-row`;
const StyledTR = tw.tr`flex w-full relative gap-[2px]`;
const StyledTH = tw.th`text-[11px] text-text-and-icon-muted leading-normal font-semibold px-[4px] py-[3.5px] border-r border-solid border-gray-1 last:border-r-0 flex-1 truncate`;
const StyledTD = tw.td`truncate relative text-[13px] text-text-and-icon-muted flex-1 flex items-center`;
const PaginationWrapper = tw.tr`w-full flex flex-row items-center justify-end`;

export const EpisodesTable = () => {
  const { t } = useTranslation();
  const { selectedShow, setSelectedEpisode } = useInsightsContext();
  const { colorBlindMode } = useMyLibraryContext();
  const {
    showSideBar,
    selectedShowDetailsGarmCategory,
    selectedShowDetailsGarmRisk,
    selectedShowDetailsEntity,
    selectedShowDetailsCategory,
    showLast90Days,
    searchTerm,
    sort,
    setShowSideBar,
    setSelectedShowDetailsGarmCategory,
    setSelectedShowDetailNamedEntity,
    setSelectedShowDetailsCategory,
    setShowLast90Days,
    setSearchTerm,
    setSort,
    tableView,
    setTableView,
    setPageNumber,
    selectedEpisodesTableColumns,
  } = useShowDetailsContext();

  const {
    episodes,
    isLoading: isShowLoading,
    isEpisodesLoading,
    episodesTotalCount,
    isFetchingNextDataPage,
  } = useShowDetailsTableData();

  const {
    currentPage: audiosCurrentPage,
    paginationRange: audiosPaginationRange,
    totalPages: audiosTotalPages,
    goToNextPage: audiosGoToNextPage,
    goToPreviousPage: audiosGoToPreviousPage,
    goToPage: audiosGoToPage,
  } = usePagination({
    data: episodes || [],
    itemsPerPage: 20,
    totalCount: episodesTotalCount,
    filter: searchTerm,
  });

  const [idsForShowAllLabels, setIdsForShowAllLabels] = useState<string[]>([]);

  const { isEnabled: isEpisodeDetailsHidden } = useFeatureFlag(FEATURE_FLAG.HIDE_EPISODE_DETAILS);

  useEffect(() => {
    setIdsForShowAllLabels([]);
  }, [tableView]);

  useEffect(() => {
    setPageNumber(String(audiosCurrentPage));
  }, [audiosCurrentPage]);

  const tableData = useMemo(() => {
    if (tableView === TABLE_VIEW.INFO) {
      return episodes?.map((episode) => ({
        [SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.EPISODES]: {
          id: episode.details.id,
          title: episode.details.title,
          thumbnail_url: episode.details.coverImage,
        },
        [SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.PUBLISHED_DATE]:
          new Date(episode.publishDate).toLocaleDateString() || '-/-',
        [SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.DURATION]: episode?.details?.duration
          ? convertSecondsToHMS(episode.details.duration)
          : '-/-',
      }));
    }

    if (tableView === TABLE_VIEW.GARM) {
      return episodes?.map((episode) => ({
        [SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.EPISODES]: {
          id: episode.details.id,
          title: episode.details.title,
          thumbnail_url: episode.details.coverImage,
        },
        [SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.PUBLISHED_DATE]:
          new Date(episode.publishDate).toLocaleDateString() || '-/-',
        ...episode.garm,
      }));
    }

    if (tableView === TABLE_VIEW.CONTEXTUAL) {
      return episodes?.map((episode) => ({
        [SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.EPISODES]: {
          id: episode.details.id,
          title: episode.details.title,
          thumbnail_url: episode.details.coverImage,
        },
        [SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.PUBLISHED_DATE]:
          new Date(episode.publishDate).toLocaleDateString() || '-/-',
        [SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.CONTEXTUAL]: episode.contextual,
      }));
    }

    if (tableView === TABLE_VIEW.ENTITIES) {
      return episodes?.map((episode) => ({
        [SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.EPISODES]: {
          id: episode.details.id,
          title: episode.details.title,
          thumbnail_url: episode.details.coverImage,
        },
        [SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.PUBLISHED_DATE]:
          new Date(episode.publishDate).toLocaleDateString() || '-/-',
        [SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.ENTITIES]: episode.linked_named_entities,
      }));
    }
  }, [episodes, tableView]);

  const handleSort = (key: AudiosSortKeyType) => {
    setSort({
      sort_field: key,
      sort_order: sort.sort_order === SORT_ORDER.ASC ? SORT_ORDER.DESC : SORT_ORDER.ASC,
    });
  };

  const getTableColumns = useCallback(
    (
      key: string
    ):
      | {
          key: string;
          align: 'left' | 'right' | 'center';
          header: JSX.Element;
          cellRender: (
            data: any,
            id?: string,
            idsForShowAllLabels?: string[],
            setIdsForShowAllLabels?: (ids: string[]) => void
          ) => JSX.Element | undefined;
        }
      | undefined => {
      // BRAND_SAFETY COLUMNS GARM
      if (Object.values(GARM_CATEGORIES).includes(key as CategoryType)) {
        return {
          key: key,
          align: 'left',
          header: <>{t(`insights.my_library_page.table.${key}`)}</>,
          cellRender: (data) => BrandSafetyCell(data as string, colorBlindMode),
        };
      }

      switch (key) {
        case SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.EPISODES:
          return {
            key,
            align: 'left',
            header: (
              <SortableHead
                title={t(`insights.my_library_page.table.${key}`, { count: episodesTotalCount })}
                isVisible={sort.sort_field === AUDIOS_SORTABLE_KEY.TITLE}
                tooltip='Sort by episode title'
                order={sort.sort_order}
                onClick={() => handleSort(AUDIOS_SORTABLE_KEY.TITLE)}
              />
            ),
            cellRender: (data) =>
              DetailsCell(
                data,
                tableView as TableViewType,
                isEpisodeDetailsHidden ? null : setSelectedEpisode
              ),
          };

        case SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.CONTEXTUAL:
          return {
            key,
            align: 'left',
            header: <>{t(`insights.my_library_page.table.${key}`)}</>,
            cellRender: (data, id, idsForShowAllLabels, setIdsForShowAllLabels) =>
              ContextualDataCell(data, id, idsForShowAllLabels, setIdsForShowAllLabels),
          };

        case SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.ENTITIES:
          return {
            key: SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.ENTITIES,
            align: 'left',
            header: <>{t(`insights.my_library_page.table.${key}`)}</>,
            cellRender: (data, id, idsForShowAllLabels, setIdsForShowAllLabels) =>
              EntitiesDataCell(data, id, idsForShowAllLabels, setIdsForShowAllLabels),
          };

        case SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.PUBLISHED_DATE:
          return {
            key,
            header: (
              <SortableHead
                title={t(`insights.my_library_page.table.${key}`)}
                isVisible={sort.sort_field === AUDIOS_SORTABLE_KEY.PUBLISHED_DATE}
                tooltip={`Sort by ${t(`insights.my_library_page.table.${key}`)}`}
                order={sort.sort_order}
                onClick={() => handleSort(AUDIOS_SORTABLE_KEY.PUBLISHED_DATE)}
              />
            ),
            align: 'left',
            cellRender: (data) => BaseCell(data),
          };

        case SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.DURATION:
          return {
            key,
            header: (
              <SortableHead
                title={t(`insights.my_library_page.table.${key}`)}
                isVisible={sort.sort_field === AUDIOS_SORTABLE_KEY.DURATION}
                tooltip={`Sort by ${t(`insights.my_library_page.table.${key}`)}`}
                order={sort.sort_order}
                onClick={() => handleSort(AUDIOS_SORTABLE_KEY.DURATION)}
              />
            ),
            align: 'left',
            cellRender: (data) => BaseCell(data),
          };
      }
    },
    [tableView, colorBlindMode, episodesTotalCount, sort, isEpisodeDetailsHidden]
  );

  const tableColumns = useMemo(
    () =>
      selectedEpisodesTableColumns[tableView]
        .filter((col) => col.isVisible)
        .map((col) => getTableColumns(col.key)),
    [selectedEpisodesTableColumns, getTableColumns, tableView]
  );

  const [isSearchFocused, setSearchIsFocused] = useState<boolean>(false);
  const hasFullWidth = isSearchFocused || searchTerm.length > 0;

  const tableRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (tableRef.current) {
      tableRef.current.scrollTop = 0;
    }
  }, [selectedShow]);

  return (
    <TableWrapper>
      {(isEpisodesLoading || isShowLoading) && (
        <LoadingOverlay data-testid='table-loader'>
          <Loader theme='dark' size='lg' />
        </LoadingOverlay>
      )}

      <TableTitle>
        <div tw='flex items-center gap-[8px]'>
          <TabSwitch
            tabs={TAB_SWITCH_TABS}
            selectedTab={tableView}
            setSelectedTab={(val) => setTableView(val as ShowDetailsTableView)}
          />
          <Separator />
          <SearchBar
            tw='w-[28px] h-[28px] text-xs font-normal bg-neutral-background px-[5px]'
            css={[hasFullWidth && tw`w-[235px]`, showSideBar && tw`mr-3`]}
            placeholder='Search for a specific episode'
            onChange={(val) => setSearchTerm(val as string)}
            inputStyles={tw`text-xs`}
            value={searchTerm}
            withSeparator={isSearchFocused || searchTerm.length > 0}
            onFocus={() => setSearchIsFocused(true)}
            onBlur={() => setSearchIsFocused(false)}
          />
        </div>
        <div tw='flex h-full items-center gap-3 transition-all duration-300'>
          {selectedShowDetailsGarmCategory ? (
            <>
              <QueryLabelContainer>
                <QueryLabel>
                  {t(`insights.garm_category.${selectedShowDetailsGarmCategory}`)}
                </QueryLabel>
                {selectedShowDetailsGarmRisk && !Array.isArray(selectedShowDetailsGarmRisk) && (
                  <BrandSafetyRiskBadge level={selectedShowDetailsGarmRisk} isTextual />
                )}
                <CloseButton onClick={() => setSelectedShowDetailsGarmCategory(null)}>
                  <CloseIcon tw='fill-text-and-icon h-[8px] w-[8px]' />
                </CloseButton>
              </QueryLabelContainer>
            </>
          ) : null}
          {selectedShowDetailsEntity ? (
            <>
              <QueryLabelContainer>
                <QueryLabel>{selectedShowDetailsEntity.value}</QueryLabel>
                <CloseButton onClick={() => setSelectedShowDetailNamedEntity(null)}>
                  <CloseIcon tw='fill-text-and-icon h-[8px] w-[8px]' />
                </CloseButton>
              </QueryLabelContainer>
            </>
          ) : null}
          {selectedShowDetailsCategory && selectedShowDetailsCategory?.display_name?.length ? (
            <>
              <QueryLabelContainer>
                <QueryLabel>{selectedShowDetailsCategory.display_name}</QueryLabel>
                <CloseButton onClick={() => setSelectedShowDetailsCategory(null)}>
                  <CloseIcon tw='fill-text-and-icon h-[8px] w-[8px]' />
                </CloseButton>
              </QueryLabelContainer>
            </>
          ) : null}
          {showLast90Days && (
            <QueryLabelContainer>
              <ToolTip
                placement='top'
                content={
                  <TooltipContent>
                    {t('insights.show_details_drawer.episodes_table.tooltip.90_day_score')}
                  </TooltipContent>
                }>
                <QueryLabel tw='flex items-center'>
                  {t('insights.show_details_drawer.episodes_table.header.90_day_score')}
                  <Info tw='stroke-text-and-icon min-w-[12px] ml-1' />
                </QueryLabel>
              </ToolTip>

              <CloseButton onClick={() => setShowLast90Days(false)}>
                <CloseIcon tw='fill-text-and-icon h-[8px] w-[8px]' />
              </CloseButton>
            </QueryLabelContainer>
          )}
          {selectedShowDetailsGarmCategory ||
          selectedShowDetailsEntity ||
          (!!selectedShowDetailsCategory && selectedShowDetailsCategory.display_name.length) ||
          showLast90Days ? (
            <Separator />
          ) : null}

          {!showSideBar && (
            <Sidebar.Toggler onClick={() => setShowSideBar(!showSideBar)} tw='shadow-dropdown' />
          )}

          <ExportSelect />
          <ColumnsFilterMenu />
        </div>
      </TableTitle>
      <TableInnerWrapper ref={tableRef}>
        {!isEpisodesLoading &&
          !isFetchingNextDataPage &&
          !episodes.length &&
          (searchTerm ? <NoTableData.EpisodeSearchResult /> : <NoTableData.Episode />)}
        <StyledTable>
          <StyledTHead>
            <StyledTR tw='z-30'>
              <StyledTH tw='flex justify-between items-center px-[24px] overflow-visible'></StyledTH>
            </StyledTR>
            <StyledTR>
              {tableColumns?.map((column, index) => (
                <StyledTH
                  title={t(`insights.my_library_page.table.${column?.key}`)}
                  key={index}
                  style={{ textAlign: column?.align as 'left' | 'right' }}
                  css={[
                    column?.key === SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.EPISODES &&
                      tw`min-w-[247px] max-w-[246px] pl-[24px]`,
                    column?.key === SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.PUBLISHED_DATE &&
                      tw`max-w-[120px]`,
                    column?.key === SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.DURATION &&
                      tw`max-w-[100px]`,
                  ]}>
                  {column?.header}
                </StyledTH>
              ))}
            </StyledTR>
          </StyledTHead>
          <StyledTBody>
            {tableData?.map((row, index: number) => (
              <StyledTR
                key={index}
                css={[
                  (tableView === 'info' ||
                    tableView === 'contextual' ||
                    tableView === 'entities') &&
                    tw`odd:bg-gray-2 even:bg-neutral-background`,
                ]}>
                {tableColumns.map((col, index) => {
                  if (!col?.key || !row?.[col?.key as keyof typeof row]) return null;

                  const key = col.key;
                  const value = row[key as keyof typeof row];
                  const details =
                    row?.[SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.EPISODES as keyof typeof row];
                  const id =
                    typeof details === 'object' && details !== null && 'id' in details
                      ? details.id
                      : undefined;

                  return (
                    <StyledTD
                      key={index}
                      css={[
                        key === SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.EPISODES &&
                          tw`min-w-[246px] max-w-[246px]`,
                        key === SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.PUBLISHED_DATE &&
                          tw`max-w-[120px]`,
                        key === SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.PUBLISHED_DATE &&
                          tableView === 'brand_safety_garm' &&
                          tw`bg-gray-2 rounded my-[1px]`,
                        key === SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.DURATION &&
                          tw`max-w-[100px]`,
                      ]}>
                      {col?.cellRender
                        ? col?.cellRender(value, id, idsForShowAllLabels, setIdsForShowAllLabels)
                        : String(row?.[col?.key as keyof typeof row])}
                    </StyledTD>
                  );
                })}
              </StyledTR>
            ))}
          </StyledTBody>
          <StyledTFoot>
            <BrandSafetyLegend type={tableView as TableViewType} />

            <PaginationWrapper>
              <td tw='flex items-end gap-[16px]'>
                {(isEpisodesLoading || isFetchingNextDataPage) && <Loader size='sm' theme='dark' />}

                <Pagination
                  currentPage={audiosCurrentPage}
                  totalPages={audiosTotalPages}
                  paginationRange={audiosPaginationRange}
                  onPageChange={(num) => audiosGoToPage(num)}
                  onNext={audiosGoToNextPage}
                  onPrevious={audiosGoToPreviousPage}
                />
              </td>
            </PaginationWrapper>
          </StyledTFoot>
        </StyledTable>
      </TableInnerWrapper>
    </TableWrapper>
  );
};

const ColumnsFiltersWrapper = tw.div`relative`;
const ColumnFilterItem = tw.button`w-full flex items-center justify-between text-text-and-icon text-[14px] leading-[140%] font-semibold py-[6px] px-[8px] [&>span]:block [&>span]:text-left [&>span]:w-full [&>span]:truncate`;
const ColumnsFiltersButton = tw.button`text-[11px] font-normal text-text-and-icon flex items-center justify-between transition-all duration-200 border border-solid border-gray-1 rounded-[4px] cursor-pointer px-[8px] h-[26px]`;
const ColumnsFiltersContainer = tw.div`absolute top-[30px] right-0 bg-neutral-background border border-solid border-gray-1 rounded-[6px] shadow-modal p-[4px] w-[276px]`;

const ColumnsFilterMenu = () => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  const dropdownRef = useRef(null);

  const { selectedEpisodesTableColumns, toggleColumnVisibility, tableView } =
    useShowDetailsContext();

  useOnClickOutside(dropdownRef, () => setIsExpanded(false));

  const handleClick = useCallback(
    (key: string) => {
      toggleColumnVisibility(key);
    },
    [toggleColumnVisibility]
  );

  if (tableView !== 'brand_safety_garm') return null;

  return (
    <ColumnsFiltersWrapper>
      <ColumnsFiltersButton onClick={() => setIsExpanded(!isExpanded)} className='group'>
        <TableIcon tw='stroke-text-and-icon-muted' />
      </ColumnsFiltersButton>

      {isExpanded && (
        <ColumnsFiltersContainer ref={dropdownRef}>
          {selectedEpisodesTableColumns[tableView]
            .filter((column) => column.key !== SHOW_DETAILS_EPISODES_TABLE_COLUMN_KEYS.EPISODES)
            .map((column) => (
              <ColumnFilterItem onClick={() => handleClick(column.key)} key={column.key}>
                <span>{t(`insights.my_library_page.table.${column.key}`)}</span>
                {column.isVisible && <CheckMarkIcon tw='stroke-accent-1' />}
              </ColumnFilterItem>
            ))}
        </ColumnsFiltersContainer>
      )}
    </ColumnsFiltersWrapper>
  );
};
